import { Injectable } from "@angular/core"
import { Headers, RequestOptions, Response, ResponseContentType } from "@angular/http"
// use storage plugin because safari private doesnot allow object to be stored in localstorage
import storage from "local-storage-fallback"
import { each, isEmpty, isString } from "lodash"

export interface ApiError {
  message: string
  code?: string
  status_code: number
}

@Injectable()
export class ToolService {
  // trims space including bom and nbsp
  TRIM_REGEX = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g
  ESCAPE_REGEX = /([.*+?^=!:${}()|\[\]\/\\])/g
  PHONE_FORMAT_REGEX = /[()-\s]/g
  ANDROID_REGEX = /Android/gi
  IOS_REGEX = /iPad|iPhone|iPod/gi
  DYNAMIC_VALUE_REGEX = /\[([^\[\]]+)\]/gi

  public EMAIL_VALIDATION_REGEX =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/
  public PHONE_VALIDATION_REGEX = /^(?:\+*\(*\)*\s*-*\d){9,15}$/

  constructor() {}

  public getApiOptions() {
    return new RequestOptions({
      headers: new Headers({ Accept: "application/json" }),
      responseType: ResponseContentType.Json
    })
  }

  public parseApiError(response: Response, default_error_message?: string): ApiError {
    let message
    const body = response.json()

    if (body && body.message) {
      message = body.message
    }

    const api_error = <ApiError>{
      message: message || default_error_message || "Unknown Error",
      status_code: response.status
    }

    if (body && body.errors && body.errors.length > 0) {
      const error: any = body.errors[0]
      api_error.code = error.code
    }

    return api_error
  }

  loadScript(url: string) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.type = "text/javascript"
      script.src = url
      script.onload = function () {
        return resolve()
      }

      const first_script = document.getElementsByTagName("script")[0]
      first_script.parentNode.insertBefore(script, first_script)
    })
  }

  public set(obj: Object, path: string, value: any) {
    let i, key
    const segments = path.split(".")

    for (i = 0; i < segments.length; i++) {
      key = segments[i]

      if (i === segments.length - 1) {
        continue
      }

      if (obj[key] === undefined) {
        obj[key] = {}
      }

      obj = obj[key]
    }

    obj[key] = value
  }

  trimString(string) {
    if (typeof string !== "string") {
      return string
    }
    return string.replace(this.TRIM_REGEX, "")
  }

  public getPhoneBaseValue(phone: string) {
    return phone.replace(this.PHONE_FORMAT_REGEX, "")
  }

  public getItemFromLocalStorage(key: string) {
    try {
      return storage.getItem(key)
    } catch (ex) {
      console.warn(`problem retrieving ${key} from localStorage`, ex)
      return undefined
    }
  }

  public setItemInLocalStorage(key: string, value: any) {
    try {
      storage.setItem(key, value)
      return true
    } catch (ex) {
      console.warn(`problem storing ${key} in localStorage`, ex)
      return false
    }
  }

  public getObjectFromLocalStorage(key: string) {
    const value = this.getItemFromLocalStorage(key)

    if (isEmpty(value)) {
      return value
    }

    try {
      return JSON.parse(value)
    } catch (ex) {
      console.warn(`problem parsing ${key} in localStorage`, ex)
      return undefined
    }
  }

  public setObjectInLocalStorage(key: string, value: any) {
    if (value) {
      value = JSON.stringify(value)
    }

    return this.setItemInLocalStorage(key, value)
  }

  public removeItemFromLocalStorage(key: string) {
    try {
      storage.removeItem(key)
    } catch (ex) {
      console.warn(`problem removing ${key} from localStorage`, ex)
    }
  }

  public escapeRegExp(string) {
    return string.replace(this.ESCAPE_REGEX, "\\$1")
  }

  // Replaces all instances of search in the string with the replace string.
  public replaceAll(string: string, search: string, replace: string, case_insensitive?: boolean) {
    let regex_options = "g"

    if (case_insensitive) {
      regex_options += "i"
    }

    return string.replace(new RegExp(this.escapeRegExp(search), regex_options), replace)
  }

  replaceDynamicValues(string: string, values: { [search: string]: string }) {
    if (!string || !isString(string)) {
      return string
    }

    const mapping = {}
    each(values, (value, search) => {
      mapping[search.toLowerCase()] = value
    })

    return string.replace(this.DYNAMIC_VALUE_REGEX, (match) => {
      const lower_case_match = match.toLowerCase()
      if (mapping[lower_case_match]) {
        return mapping[lower_case_match]
      }
      return match
    })
  }

  isAndroidDevice() {
    const userAgent = window.navigator.userAgent || window.navigator.vendor
    return this.ANDROID_REGEX.test(userAgent)
  }

  isIOSDevice() {
    const userAgent = window.navigator.userAgent || window.navigator.vendor
    return this.IOS_REGEX.test(userAgent)
  }

  isSMSEnabled() {
    return this.isAndroidDevice() || this.isIOSDevice()
  }
}



// WEBPACK FOOTER //
// /home/runner/work/onelocal/onelocal/src/my-referral/src/app/tool.service.ts